import { generatePath } from 'react-router-dom';
import { ApiCacheTags, baseApiService } from '@qapline/api';
import { getConfig } from '@qapline/config';
import { transformObjectToFormData } from '@qapline/helpers';

import { TGetUserProfilePictureResponseBody, TUploadUserProfilePictureRequestBody } from '../types';

const {
    profilePicture: profilePictureEndpoint,
    deleteProfilePicture: deleteProfilePictureEndpoint,
    uploadProfilePicture: uploadProfilePictureEndpoint,
} = getConfig().api.endpoints.user;
const { profilePicture: profilePictureTag } = ApiCacheTags.user;

export const userProfilePictureApi = baseApiService.injectEndpoints({
    endpoints: (builder) => ({
        getUserProfilePicture: builder.query<TGetUserProfilePictureResponseBody, number>({
            query: (userId) => {
                return {
                    url: generatePath(profilePictureEndpoint, { userId: userId.toString() }),
                    method: 'GET',
                    responseHandler: async (response) => response,
                };
            },
            transformResponse: async (response: Response) => {
                const data = await response.blob();
                const headers = response.headers;
                return { data, headers };
            },
            transformErrorResponse: async (baseQueryError) => {
                const response = baseQueryError.data as Response;
                return {
                    status: response.status,
                    data: await response.json(),
                    message: response.statusText,
                };
            },
            providesTags: (_res, error, userId) => (error ? [] : [{ type: profilePictureTag, id: userId }]),
        }),
        uploadUserProfilePicture: builder.mutation<void, TUploadUserProfilePictureRequestBody>({
            query: ({ userId, profilePicture }) => {
                const body = transformObjectToFormData({ profilePicture });

                return {
                    url: generatePath(uploadProfilePictureEndpoint, { userId: userId.toString() }),
                    method: 'PUT',
                    body,
                };
            },
            invalidatesTags: (_res, error, { userId }) => (error ? [] : [{ type: profilePictureTag, id: userId }]),
        }),
        deleteUserProfilePicture: builder.mutation<void, number>({
            query: (userId) => ({
                url: generatePath(deleteProfilePictureEndpoint, { userId: userId.toString() }),
                method: 'DELETE',
            }),
            invalidatesTags: (_res, error, userId) => (error ? [] : [{ type: profilePictureTag, id: userId }]),
        }),
    }),
});

export const {
    useGetUserProfilePictureQuery,
    useLazyGetUserProfilePictureQuery,
    useDeleteUserProfilePictureMutation,
    useUploadUserProfilePictureMutation,
} = userProfilePictureApi;
